<template>
  <div>
    <div class="mb-3 d-flex justify-content-between align-items-end">
      <h4 class="font-weight-bold m-0" :class="{ 'invisible': !!compare.index }">
        <span class="text-muted font-weight-light">Insights</span> <span class="material-icons md-14 arrow-east">east</span> Field metrics
      </h4>
      <div v-if="data && data.period && data.period.length > 0">
        <div @click="compare.add(compare.index)" class="text-primary font-weight-bold cursor-pointer">
          compare form&nbsp;
          <div class="position-absolute" style="right: -9px; top: 3px;">
            <span class="material-icons" style="color: var(--primary); rotate: 90deg; font-size: 24px;">turn_right</span>
          </div>
        </div>
        <div v-if="compare.length() > 1"
             @click="compare.remove(compare.index)"
             class="position-absolute cursor-pointer" style="right: 15px; top: 40px; z-index: 1024;"
        >
          <span class="material-icons" style="color: var(--secondary); font-size: 20px;">close</span>
        </div>
      </div>
    </div>
    <filters
      @input="reload"
      @dateChange="loadDimensions(true)"
      @formChange="loadDimensions(true)"
      :dimensions="dimensions" ref="filters">
      <template v-slot:buttons>
        <AiAnalysisButton
          v-if="($root.hasRole('admin.internal') || $root.hasRole('ai.analysis')) && data && data.form && data.form[0] && $root.client && $refs.filters && $refs.filters.dates"
          :client="$root.client"
          :form="data.formData[0]"
          :from="$refs.filters.dates.from"
          :to="$refs.filters.dates.to"
        />
      </template>
    </filters>
    <template v-if="data && fieldMetricsData && fieldMetricsData.length > 0">
      <div id="fieldMetrics" ref="fieldMetrics" class="card mb-3 metrics-table" data-name="fieldMetricsSection">
        <div :style="{'background-image': `url('${bgImage2point(1, (fieldMetricsData[0].interactions-((fieldMetricsData[0].interactions - data.form[0].starts)/2)) / data.form[0][toggleStarts ? 'starts' : 'sessions'])}')`}"
             style="background-size: 100% 100%; background-repeat: no-repeat; min-width: 1700px; width: 100%; height: 236px; position: absolute; left: 0; top: 0;">
        </div>
        <div class="d-flex p-3" style="position: relative; height: 236px;">
          <div class="flex-fill">
            <i class="material-icons display-4 text-primary">timeline</i>
            <div class="font-weight-bold">Sessions <i class="material-icons filter" v-if="filtersApplied" v-b-tooltip title="Not all data is shown as there are one or more filters applied">filter_alt</i></div>
            <div class="font-weight-bolder letter-spacing-1px text-primary text-xlarge">{{ data.form[0].sessions | numeral }}</div>
            <div v-if="data.form[0].start_rate < 100" style="color: #AEAEAE;">{{ data.form[0].starts | numeral }} visitors or {{ data.form[0].start_rate }}% started<br />interacting with the form</div>
            <div v-else style="color: #AEAEAE;">All visitors started interacting with the form</div>
          </div>
          <div class="text-center pt-4 pb-5" style="width: 250px;">
            <span class="badge badge-pill text-white bg-primary text-uppercase" style="transform: scale(0.8)">initial abandoners</span>
            <div class="font-weight-bolder letter-spacing-1px text-primary text-xlarge">
              <i class="material-icons" style="font-size: 18px; vertical-align: middle;">south</i>
              {{ (data.form[0].sessions - data.form[0].starts) / data.form[0].sessions | numeral('0,0.0%') }}
            </div>
            <span style="color: #AEAEAE;">
              {{ (data.form[0].sessions - data.form[0].starts) | numeral }} visitors left the page<br />without interacting with the form
            </span>
            <div class="mt-3 small text-primary text-right align-text-bottom" v-if="isOverflown()">Scroll right for more insights <span class="material-icons md-14 arrow-east">east</span></div>
          </div>
        </div>
        <div ref="fieldMetricsHeaderAnchor" class="fieldMetricsHeaderAnchor"></div>
        <div
          ref="fieldMetricsHeader"
          class="fieldMetricsHeader"
          :class="{ 'fieldMetricsHeader--fixed': stickyHeader.isActive, 'fieldMetricsFirstColumn--scrolled': fieldMetricsHasHorizontalScroll }"
          :style="fieldMetricsHeaderStyle"
          style="font-size: 10.4px; margin-top: -24px; color: #AEAEAE; white-space:nowrap; min-width: max-content;"
        >
          <div
            class="d-flex font-weight-bold text-center p-1 fieldMetricsHeaderContent fieldMetricsRow"
            :class="{ 'fieldMetricsFirstColumn--scrolled': fieldMetricsHasHorizontalScroll }"
            :style="fieldMetricsHeaderContentStyle"
            style="padding-left: 0 !important;"
          >
            <div
              class="flex-fill text-right fieldMetricsFirstColumn fieldMetricsHeaderFirstColumn pr-2"
              :style="fieldMetricsHeaderFirstColumnStyle"
              :class="{ 'fieldMetricsFirstColumn--scrolled': fieldMetricsHasHorizontalScroll }"
              style="min-width: 300px;"
            >
              <span v-b-tooltip title="Fields are displayed in the order how the average user fills in your form">FORM FIELD</span>
            </div>
            <div v-if="typeof $route.query.verbosity !== 'undefined'" style="min-width: 200px;">
              FIELD INFO
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="An interaction is defined as starting to fill out your form (fill a field, change a checkbox/dropbown/etc). Selecting or moving between fields without changing its data is not considered as a start-activity.">
              INTERACTIONS
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The last field that was changed by a visitor that did not convert.">
              DROPOFFS
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The average amount of time that visitors need to fill-in and correct your field.">
              INTERACTION TIME
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The average amount of time that visitors spend on your form before moving on without filling out the field(s). This includes any hesitation, searching for what to do next, etc.">
              HESITATION TIME
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="A correction is only considered valid when a visitor revisits a field after making an initial change.">
              CORRECTIONS
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The amount of visitors that received an error on this field.">
              ERRORS
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The amount of times autofill or autocomplete was used for filling in your field.">
              AUTOFILLS
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The amount of visitors that rage clicked this field.">
              RAGE CLICKS
            </div>
            <div style="min-width: 150px;" v-b-tooltip title="The amount of times visitors ignored or skipped your field by not interacting with it.">
              BLANKS
            </div>
            <div style="min-width: 50px;">
              &nbsp;
            </div>
          </div>
        </div>
        <div
          v-if="stickyHeader.isActive"
          class="fieldMetricsHeaderSpacer"
          :style="{ height: `${stickyHeader.spacerHeight}px` }"
        ></div>
        <div v-for="(item, index) in fieldMetricsData" :key="index" class="metrics-rows" :class="item.isGroup ? 'isGroup' : ''">
          <div class="d-flex text-center cursor-pointer fieldMetricsRow"
               :ref="`field-${item.element.id}`"
               @click="(!item.isGroup && (details.expanded = (details.expanded !== index) ? index : null)) || (item.isGroup && toggleElementGroup(item.form_element_group_hash))"
               :style="{'background-image': `url('${bgImage3point((item.prev.interactions-((item.prev.interactions - item.interactions)/2)) / data.form[0][toggleStarts ? 'starts' : 'sessions'], item.interactions / data.form[0][toggleStarts ? 'starts' : 'sessions'], (item.next.interactions-((item.next.interactions - item.interactions)/2)) / data.form[0][toggleStarts ? 'starts' : 'sessions'])}')` }"
               style="background-size: 100% 100%; background-repeat: no-repeat;">
            <div
              style="height: 68px;"
              class="flex-fill text-right text-primary font-weight-bolder letter-spacing-1px fieldMetricsFirstColumn"
              :class="{
                'fieldMetricsFirstColumn--scrolled': fieldMetricsHasHorizontalScroll,
                'has-dropoff-light': hasDropoff(item, 0.10),
                'has-dropoff': hasDropoff(item, 0.25),
                'text-danger': hasDropoff(item, 0.10)
              }"
              :style="{ minWidth: '300px' }"
            >
              <div class="position-absolute" style="width: 4px; height: 68px; left: 0; top: 0;" :style="{'background-color': item.rowBorderColor}" v-if="item.collapsed === false"></div>
              <div class="metrics-rows-item">
                <span class="ellide-name" :style="!item.isGroup && item.form_element_group_hash ? 'font-size: 0.75rem; padding-right: 2rem;' : ''">
                  <i v-if="!item.isGroup && item.element?.status !== 'ACTIVE'" class="ion ion-md-eye-off text-secondary" v-b-tooltip title="Field was hidden during form setup"></i>
                  <span :id="item.element?.id ?? item.element?.name" :class="{'text-secondary': !item.isGroup && item.element?.status !== 'ACTIVE'}">
                    {{ ( item.element ? item.element.display_name : '' ) }}
                  </span>
                  <span class="pr-2">
                    <button v-if="item.isGroup" class="btn btn-create btn-sm pointer-events-disabled" style="padding: 0 2px; line-height: 0.65rem; vertical-align: text-top; width: 16px; height: 16px; margin-right: 1px;">
                      <span class="font-weight-light">{{ item.collapsed ? '+' : '-' }}</span>
                    </button>
                    <span v-else>
                      <img width="24" height="24" style="vertical-align: bottom;" :style="item.element?.status !== 'ACTIVE' ? 'filter: grayscale(1);' : ''" :src="elementTypes[item.element.form_element_type_hash]?.logo_url || elementTypePlaceholder" :alt="elementTypes[item.element.form_element_type_hash]?.display_name || ''" v-b-tooltip="{ delay: 250 }" :title="elementTypes[item.element.form_element_type_hash]?.description || ''"/>
                    </span>
                  </span>
                </span>
                <b-tooltip v-if="!item.isGroup" :target="item.element?.id ?? item.element?.name" placement="left" triggers="hover">
                  Fields are displayed in the order how the average user fills in your form
                </b-tooltip>
              </div>
              <!-- Indicate there are field hidden -->
              <div v-if="index === (fieldMetricsData.length - 1) && (hiddenFields.length || showHiddenFields)"
                   @click.stop="showHiddenFields = !showHiddenFields"
                   class="cursor-pointer small text-secondary"
                   style="position: absolute; bottom: -28px; right: 0;"
              >
                <i class="ion ion-md-eye-off text-secondary" style="color: var(--secondary) !important;" v-b-tooltip title="Field was hidden during form setup"></i>
                {{ showHiddenFields ? 'Hide' : 'Show' }} {{ hiddenFields.length }} hidden {{ hiddenFields.length > 1 ? 'fields' : 'field' }}
              </div>
            </div>
            <div v-if="typeof $route.query.verbosity !== 'undefined'"
                 style="min-width: 200px; height: 68px;" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }"
                 class="d-flex flex-column justify-content-center small">
              <div><b>Type:</b> {{ elementTypes[item.element.form_element_type_hash]?.name || '' }}</div>
              <div v-if="item.element.category"><b>Categorisation:</b> {{ item.element.category?.name }}</div>
            </div>
            <div style="min-width: 150px; height: 68px;" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item">
                <b v-b-tooltip="data.form[0][toggleStarts ? 'starts' : 'sessions'] && $options.filters.numeral(item.interactions / data.form[0][toggleStarts ? 'starts' : 'sessions'], '0,0.0%')">{{ item.interactions }}</b>
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item">
                <div class="letter-spacing-1px font-weight-bolder letter-spacing-1px">
                  <router-link
                    v-if="item.dropoffs && getSessionRecordingsForElement(item.element)"
                    @click.native="$event.stopPropagation()"
                    :to="$refs.filters.routeBuilder({ name: 'session-replays-for-form', params: { id: $refs.filters.item.id }, query: {filters: {dropoff_elements: [item.element?.name ?? item.element?.identifier]}}})"
                    v-b-tooltip="{ delay: 600 }" title="Session replays available"
                  >
                    <span class="material-symbols-outlined filled cursor-pointer" :class="hasDropoff(item, 0.10) ? 'text-danger' : 'text-secondary'" style="font-size: 0.85rem; vertical-align: middle;">videocam</span>
                  </router-link>
                  <i v-else class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;" v-b-tooltip :title="item.dropoffs + ' dropoff' + ((item.dropoffs > 1 || !item.dropoffs) ? 's' : '')">
                    logout
                  </i>
                  <span v-b-tooltip :title="item.dropoffs + ' dropoff' + ((item.dropoffs > 1 || !item.dropoffs) ? 's' : '')">
                    {{ (item.dropoffs / item.interactions) | numeral('0,0.0%') }}
                  </span>
                </div>
                <span class="small" v-if="item.dropoffs" v-b-tooltip="{ delay: 600 }" :title="item.dropoffs + ' visitor' + (item.dropoffs > 1 ? 's' : '') + ' abandoned the form after changing this field.'">{{ item.dropoffs }} visitor{{ item.dropoffs > 1 ? 's' : '' }} dropped off</span>
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item">
                <i class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;">schedule</i>
                {{ item.interaction_time | timeSeconds }}
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item">
                <i class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;">more_time</i>
                {{ item.hesitation_time | timeSeconds }}
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item"
                   :class="{'text-danger': (item.corrections / item.interactions) > 0.25}">
                <router-link
                  v-if="item.corrections && getSessionRecordingsForElement(item.element)"
                  @click.native="$event.stopPropagation()"
                  :to="$refs.filters.routeBuilder({ name: 'session-replays-for-form', params: { id: $refs.filters.item.id }, query: {filters: {elements: [item.element?.name ?? item.element?.identifier], sort_by: [{ column: 'correction_rate', direction: 'DESC', hide_zeroes: true }]}}})"
                  v-b-tooltip="{ delay: 600 }" title="Session replays available"
                >
                  <span class="material-symbols-outlined filled cursor-pointer" :class="hasDropoff(item, 0.10) ? 'text-danger' : 'text-secondary'" style="font-size: 0.85rem; vertical-align: middle;">videocam</span>
                </router-link>
                <i v-else
                   class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;"
                   :class="{'text-danger': (item.corrections / item.interactions) > 0.25}"
                   v-b-tooltip :title="item.corrections + ' correction' + ((item.corrections > 1 || !item.corrections) ? 's' : '')">
                  restart_alt
                </i>
                <template v-if="togglePercentages">{{ item.corrections }} <small>of {{ item.interactions }}</small></template>
                <span v-else v-b-tooltip :title="item.corrections + ' correction' + ((item.corrections > 1 || !item.corrections) ? 's' : '')">
                  {{ (item.corrections / item.interactions) | numeral('0,0.0%') }}
                </span>
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item"
                   :class="{'text-danger': (item.errors / item.interactions) > 0.25}">
                <router-link
                  v-if="item.errors && getSessionRecordingsForElement(item.element)"
                  @click.native="$event.stopPropagation()"
                  :to="$refs.filters.routeBuilder({ name: 'session-replays-for-form', params: { id: $refs.filters.item.id }, query: {filters: {elements: [item.element?.name ?? item.element?.identifier], sort_by: [{ column: 'error_rate', direction: 'DESC', hide_zeroes: true }]}}})"
                  v-b-tooltip="{ delay: 600 }" title="Session replays available"
                >
                  <span class="material-icons cursor-pointer" :class="hasDropoff(item, 0.10) || (item.errors / item.interactions) > 0.25 ? 'text-danger' : 'text-secondary'" style="font-size: 0.85rem; vertical-align: middle;">videocam</span>
                </router-link>
                <i v-else class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;"
                   :class="{'text-danger': (item.errors / item.interactions) > 0.25}"
                   v-b-tooltip :title="item.errors + ' error' + ((item.errors > 1 || !item.errors) ? 's' : '')">highlight_off</i>
                <template v-if="togglePercentages">{{ item.errors }} <small>of {{ item.interactions }}</small></template>
                <span v-else v-b-tooltip :title="item.errors + ' error' + ((item.errors > 1 || !item.errors) ? 's' : '')">
                  {{ ((item.errors) ? ((item.errors / item.interactions)) : '0') | numeral('0,0.0%') }}
                </span>
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item" v-b-tooltip :title="item.autofills + ' autofill' + ((item.autofills > 1 || !item.autofills) ? 's' : '')">
                <i class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;">auto_fix_high</i>
                <template v-if="togglePercentages">{{ item.autofills }} <small>of {{ item.interactions }}</small></template>
                <template v-else>{{ (item.autofills / item.interactions) | numeral('0,0.0%') }}</template>
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item" :class="{'text-danger': (item.rage_clicks / item.interactions) > 0.25}">
                <router-link
                  v-if="item.rage_clicks && getSessionRecordingsForElement(item.element)"
                  @click.native="$event.stopPropagation()"
                  :to="$refs.filters.routeBuilder({ name: 'session-replays-for-form', params: { id: $refs.filters.item.id }, query: {filters: {elements: [item.element?.name ?? item.element?.identifier], sort_by: [{ column: 'field_rage_clicks_rate', direction: 'DESC', hide_zeroes: true }]}}})"
                  v-b-tooltip="{ delay: 600 }" title="Session replays available"
                >
                  <span class="material-icons cursor-pointer" :class="hasDropoff(item, 0.10) || (item.rage_clicks / item.interactions) > 0.25 ? 'text-danger' : 'text-secondary'" style="font-size: 0.85rem; vertical-align: middle;">videocam</span>
                </router-link>
                <i v-else class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;"
                   :class="{'text-danger': (item.rage_clicks / item.interactions) > 0.25}"
                   v-b-tooltip :title="item.rage_clicks + ' rage click' + ((item.rage_clicks > 1 || !item.rage_clicks) ? 's' : '')">ads_click</i>
                <template v-if="togglePercentages">{{ item.rage_clicks }} <small>of {{ item.interactions }}</small></template>
                <span v-else v-b-tooltip :title="item.rage_clicks + ' rage click' + ((item.rage_clicks > 1 || !item.rage_clicks) ? 's' : '')">
                  {{ ((item.rage_clicks) ? ((item.rage_clicks / item.interactions)) : '0') | numeral('0,0.0%') }}
                </span>
              </div>
            </div>
            <div style="min-width: 150px; height: 68px;" class="font-weight-bolder letter-spacing-1px" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item" v-b-tooltip :title="item.blanks + ' blank' + ((item.blanks > 1 || !item.blanks) ? 's' : '')">
                <i class="material-icons text-secondary d-inline-block" style="font-size: 0.85rem; vertical-align: middle;">skip_next</i>
                <template v-if="togglePercentages">{{ item.blanks }} <small>of {{ data.form[0].starts }}</small></template>
                <template v-else>{{ item.blank_rate | numeral('0,0.0%') }}</template>
              </div>
            </div>
            <div style="min-width: 50px; height: 68px;" :class="{'has-dropoff-light': hasDropoff(item, 0.10), 'has-dropoff': hasDropoff(item, 0.25), 'text-danger': hasDropoff(item, 0.10) }">
              <div class="metrics-rows-item" v-b-tooltip="{ delay: 600 }" title="Detailed trend metrics">
                <i class="material-symbols-outlined text-secondary d-inline-block" style="vertical-align: middle;">monitoring</i>
              </div>
            </div>
          </div>
          <field-metrics-details
            v-if="details.expanded === index"
            :key="index"
            :field="item.element"
            :filters-applied="filtersApplied"
            :query="details.query"
            :toggle-starts="toggleStarts"
            class="metrics-rows-item-details"
            :style="{'border-left': item.collapsed === false ? '4px solid ' + item.rowBorderColor : '0'}"
            data-name="fieldMetricsDetailsSection"
            :data-id="item.element.id"
          />
        </div>
        <div class="position-relative">
          <div :style="{'background-image': `url('${bgImage2point((fieldMetricsData[fieldMetricsData.length - 1].interactions-((fieldMetricsData[fieldMetricsData.length - 1].interactions - data.form[0].conversions)/2)) / data.form[0][toggleStarts ? 'starts' : 'sessions'], data.form[0].conversions / data.form[0][toggleStarts ? 'starts' : 'sessions'])}')`}"
               style="background-size: 100% 100%; background-repeat: no-repeat; position: absolute; left: 0; top: 0; height: 203px; min-width: 1700px; width: 100%;">
          </div>
          <div class="d-flex justify-content-between p-3 pt-5" style="height: 203px; position: relative;">
            <div>
              <i class="material-icons text-primary display-4">{{ !nextForm ? 'check' : 'keyboard_double_arrow_right' }}</i>
              <div class="font-weight-bold">{{ !nextForm ? 'Conversions' : 'Continued to next step' }} <i class="material-icons filter" v-if="filtersApplied" v-b-tooltip title="Not all data is shown as there are one or more filters applied">filter_alt</i></div>
              <div>
                <span class="font-weight-bolder letter-spacing-1px text-primary text-xlarge">{{ data.form[0].conversions | numeral }}</span>
                <span v-if="typeof $route.query.verbosity !== 'undefined'" class="text-secondary" style="font-size: small; letter-spacing: normal; font-weight: normal;">
                  or {{ data.form[0].conversions / (data.form[0][toggleStarts ? 'starts' : 'sessions']) | numeral('0,0.0%') }}
                </span>
              </div>
              <div style="color: #AEAEAE;">
                {{ data.form[0].abandoners | numeral }} abandoned or {{ data.form[0].abandonment_rate }}%<br />after starting the form
                <i v-if="(data.form[0].abandoners + data.form[0].conversions) > data.form[0][toggleStarts ? 'starts' : 'sessions']" class="material-icons info" v-b-tooltip title="Some visitors started filling out the form but left before submitting it. Later in their session they returned and converted.">info</i>
              </div>
            </div>
            <div class="text-left">
              <span class="material-icons text-primary mt-2 mb-2">highlight_off</span>
              <div class="font-weight-bold">Failed submissions <i class="material-icons filter" v-if="filtersApplied" v-b-tooltip title="Not all data is shown as there are one or more filters applied">filter_alt</i></div>
              <div>
                <span class="font-weight-bolder letter-spacing-1px text-primary text-xlarge">{{ data.form[0].submits_failed | numeral }}</span>
                <span v-if="typeof $route.query.verbosity !== 'undefined'" class="text-secondary" style="font-size: small; letter-spacing: normal; font-weight: normal;">
                  or {{ data.form[0].submits_failed / data.form[0]['starts'] | numeral('0,0.0%') }}
                </span>
              </div>
              <div style="color: #AEAEAE;">visitors submitted the form<br />but did not fill all fields correctly</div>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end text-secondary mt-3 mb-2">
        <div class="mr-3" v-b-tooltip title="Shows actual numbers instead of percentages for the 'Browser usage' and 'Device types' insights.">
          <span role="button" @click="togglePercentages = !togglePercentages">Toggle percentages <span class="material-icons" style="font-size: 16px; vertical-align: sub;">tune</span></span>
        </div>
        <div v-b-tooltip title="Calculates the conversion rate based on starts instead of sessions. This is useful for analysing situations where we have a high amount of sessions but a low amount of starts. For example homepages or pages where the form is hidden by default.">
          <label :for="'toggleStarts' + $props.compare.index" role="button">Conversion rate based on starts</label>
          <label class="switcher switcher-sm switcher-success ml-1 mr-0">
            <input type="checkbox" :id="'toggleStarts' + $props.compare.index" class="switcher-input" v-model="toggleStarts" @change="persistToggleStarts">
            <span class="switcher-indicator"><span class="switcher-yes"></span><span class="switcher-no"></span></span>
          </label>
        </div>
      </div>

      <div class="text-right">
        <span role="status" class="spinner-border text-primary d-none mr-3" style="vertical-align: middle; width: 1.5rem; height: 1.5rem;"><span class="sr-only">Loading</span></span>
        <b-dropdown split id="download" text="Download" variant="create" class="m-1 dropdown-menu-right" @click="download('xlsx')" v-if="data">
          <b-dropdown-header>Field metrics</b-dropdown-header>
          <b-dropdown-item @click="download('xlsx')" class="cursor-pointer"><span class="icon-xlsx-file" style="vertical-align: text-bottom;">&nbsp;</span> Standard dimensions</b-dropdown-item>
          <b-dropdown-item @click="download('xlsx-browser-device-country')" class="cursor-pointer"><span class="icon-xlsx-file" style="vertical-align: text-bottom;">&nbsp;</span> With browser, device and country dimensions</b-dropdown-item>
          <b-dropdown-item @click="download('csv')" class="cursor-pointer"><i class="material-icons" style="font-size: 18px; vertical-align: sub;">download</i> CSV</b-dropdown-item>
          <b-dropdown-header>Form and field metrics</b-dropdown-header>
          <b-dropdown-item @click="download('xlsx-form-and-field')" class="cursor-pointer"><span class="icon-xlsx-file" style="vertical-align: text-bottom;">&nbsp;</span> Standard dimensions</b-dropdown-item>
          <b-dropdown-item @click="download('xlsx-form-and-field-browser-device-country')" class="cursor-pointer"><span class="icon-xlsx-file" style="vertical-align: text-bottom;">&nbsp;</span> With browser, device and country dimensions</b-dropdown-item>
          <!-- <b-dropdown-item @click="download('xlsx-form-and-field-custom-dimensions')" class="cursor-pointer"><span class="icon-xlsx-file" style="vertical-align: text-bottom;">&nbsp;</span> With all custom dimensions</b-dropdown-item>-->
        </b-dropdown>
      </div>
    </template>
    <div class="card" v-else-if="loading === false">
      <div class="card-body">
        <div class="m-2 text-muted" v-if="!filtersApplied">
          <template v-if="data && data?.form">No insights found for the selected timeframe.</template>
          <template v-else-if="!data?.form">No forms found. To continue, <a href="/setup/forms/add">add</a> one now.</template>
        </div>
        <div class="m-2 text-muted" v-else>
          No form starts for the selected filters
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Filters from '@/components/insights/Filters'
import { objectSumGrouped, objectSum, download } from '@/utils'
import FieldMetricsDetails from "@/components/insights/FieldMetricsDetails.vue"
import AiAnalysisButton from './eureka/AiAnalysisButton.vue';
import { DateTime } from 'luxon'

export default {
  name: 'FieldMetrics',
  metaInfo: {
    title: 'Field metrics'
  },
  components: {FieldMetricsDetails, Filters, AiAnalysisButton },
  props: {
    compare: {
      type: Object,
      index: 0
    }
  },
  data () {
    return {
      data: {
        formHash: null,
        form: null,
        formData: null,
        period: null,
        sessionRecordingDimensions: null
      },
      fieldMetricsRaw: null,
      dimensions: {},
      filtersApplied: false,
      formElements: null,
      loading: true,
      details: {
        expanded: null,
        query: {},
      },
      togglePercentages: false,
      toggleStarts: false,
      dateFormatter: null,
      elementGroups: [],
      elementTypes: {},
      elementTypePlaceholder: 'https://assets.exatom.io/form-element-icons/icon-placeholder.svg',
      showHiddenFields: false,
      stickyHeader: {
        isActive: false,
        left: 0,
        width: 0,
        contentWidth: 0,
        scrollLeft: 0,
        height: 0,
        spacerHeight: 0
      },
      fieldMetricsScrollElement: null
    }
  },
  computed: {
    fieldMetricsHasHorizontalScroll () {
      return this.stickyHeader.scrollLeft > 0
    },
    fieldMetricsHeaderStyle () {
      if (!this.stickyHeader.isActive) return {}

      return {
        left: `${this.stickyHeader.left}px`,
        top: '0px',
        width: `${this.stickyHeader.width}px`
      }
    },
    fieldMetricsHeaderContentStyle () {
      if (!this.stickyHeader.isActive) return {}

      return {
        width: `${this.stickyHeader.width}px`,
        transform: `translateX(-${this.stickyHeader.scrollLeft}px)`
      }
    },
    fieldMetricsHeaderFirstColumnStyle () {
      if (!this.stickyHeader.isActive) return {}

      return {
        transform: `translateX(${this.stickyHeader.scrollLeft}px)`
      }
    },
    fieldMetricsData () {
      return this.data.period
        // Only show active fields/groups
        ?.filter((metric) => {
          return metric?.isGroup === true || this.showHiddenFields || (metric.element !== null && metric.element.status === 'ACTIVE')
        })
        // Form grouping
        .filter(metric => typeof metric.collapsed === 'undefined' || metric?.isGroup === true || metric.collapsed === false)
        .map(this.recalculatePrevNextFn)
    },
    hiddenFields () {
      return this.fieldMetricsRaw.filter(f => {
        const el = this.getElement(f.element_name)
        if (!el?.id) return true
        if (el?.status !== 'ACTIVE') return true
        return false
      }) ?? []
    },
    nextForm () {
      /* eslint-disable camelcase */
      if (this.$refs?.filters?.item?.next_form) {
        return this.$refs.filters.item.next_form
      }
      /* eslint-enable camelcase */
      return null
    }
  },
  methods: {
    bgImage2point (prev, next) {
      return 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100" class="graph-svg">
        <polygon points="${prev * 100} 0, ${next * 100} 100, 0 100, 0 0" fill="#D4FDF6" />
        </svg>`)
    },
    bgImage3point (prev, current, next) {
      return 'data:image/svg+xml;base64,' + btoa(`<svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 100 100" class="graph-svg">
        <polygon points="${prev * 100} 0, ${current * 100} 50, ${next * 100} 100, 0 100, 0 0" fill="#D4FDF6" />
        </svg>`)
    },
    hasDropoff (item, threshold) {
      return (item.dropoffs / item.interactions) >= threshold
    },
    async download (type) {
      const query = {
        from: DateTime.fromISO(this.$refs.filters.dates.from).toFormat('yyyy-LL-dd'),
        to: DateTime.fromISO(this.$refs.filters.dates.to).toFormat('yyyy-LL-dd'),
        forms: [this.$refs.filters.item.id],
        filters: this.$refs.filters.dimensionFilters
      }

      switch (type) {
        case 'csv':
          await download(await this.$api.post('/metrics/fields', { ...query, format: 'csv' }, this.$root.client.client_hash, { responseType: 'blob' }))
          break

        case 'xlsx':
          await download(await this.$api.post('/metrics/fields', { ...query, format: 'xlsx' }, this.$root.client.client_hash, { responseType: 'blob' }))
          break

        case 'xlsx-browser-device-country':
          await download(await this.$api.post('/metrics/fields', { ...query, format: 'xlsx', dimensions: ['browser_family', 'device_type', 'country_code'] }, this.$root.client.client_hash, { responseType: 'blob' }))
          break

        case 'xlsx-form-and-field':
          await download(await this.$api.post('/metrics/full-export', { ...query, format: 'xlsx' }, this.$root.client.client_hash, { responseType: 'blob' }))
          break

        case 'xlsx-form-and-field-browser-device-country':
          await download(await this.$api.post('/metrics/full-export', { ...query, format: 'xlsx', dimensions: ['browser_family', 'device_type', 'country_code'] }, this.$root.client.client_hash, { responseType: 'blob' }))
          break

        case 'xlsx-form-and-field-custom-dimensions':
          // TODO
          // await download(await this.$api.post('/metrics/full-export', { ...query, format: 'xlsx', dimensions: ['browser_family', 'device_type', 'country_code'] }, this.$root.client.client_hash, { responseType: 'blob' }))
          break
      }
    },
    getElement (searchElementName) {
      const baseElement = {
        form_hash: this.data.formHash,
        form_element_group_hash: null,
        name: searchElementName,
        label: searchElementName,
        display_name: searchElementName
      }

      if (!this.formElements) {
        return baseElement
      }

      // == check instead of === for fields that consist only out of numbers
      const el = this.formElements.find((element) => (element.name == searchElementName || element.identifier == searchElementName)) || baseElement

      // Add in categorisation
      el.category = this.$refs.filters.item.elements.find(fe => fe.id === el.id)?.category

      return el
    },
    isOverflown () {
      if (!this.$refs.fieldMetrics) return
      return this.$refs.fieldMetrics.scrollHeight > this.$refs.fieldMetrics.clientHeight || this.$refs.fieldMetrics.scrollWidth > this.$refs.fieldMetrics.clientWidth
    },
    syncFieldMetricsScrollListener () {
      if (this.fieldMetricsScrollElement) {
        this.fieldMetricsScrollElement.removeEventListener('scroll', this.updateStickyHeader)
        this.fieldMetricsScrollElement = null
      }

      if (!this.$refs.fieldMetrics) return

      this.fieldMetricsScrollElement = this.$refs.fieldMetrics
      this.fieldMetricsScrollElement.addEventListener('scroll', this.updateStickyHeader, { passive: true })
    },
    updateStickyHeader () {
      const container = this.$refs.fieldMetrics
      const header = this.$refs.fieldMetricsHeader
      const anchor = this.$refs.fieldMetricsHeaderAnchor

      if (!container || !header || !anchor) {
        this.stickyHeader.isActive = false
        this.stickyHeader.scrollLeft = 0
        return
      }

      const containerRect = container.getBoundingClientRect()
      const anchorRect = anchor.getBoundingClientRect()
      const headerHeight = header.offsetHeight
      const shouldStick = anchorRect.top <= 0 && containerRect.bottom > headerHeight

      this.stickyHeader.scrollLeft = container.scrollLeft

      this.stickyHeader.height = headerHeight
      this.stickyHeader.spacerHeight = Math.max(0, headerHeight - 24)

      if (!shouldStick) {
        this.stickyHeader.isActive = false
        return
      }

      this.stickyHeader.isActive = true
      this.stickyHeader.left = containerRect.left
      this.stickyHeader.width = containerRect.width
      this.stickyHeader.contentWidth = container.scrollWidth
    },
    persistToggleStarts () {
      (window.localStorage || {})[`toggleStarts_${this.data.formHash}`] = this.toggleStarts ? '1' : '0'
    },
    recalculatePrevNextFn (item, index, all) {
      if (!this.data?.form) return

      return {
        ...item,
        prev: index ? all[index - 1] : { interactions: this.data.form[0].starts },
        next: (index !== (all.length - 1)) ? all[index + 1] : { interactions: this.data.form[0].conversions },
        fill_in_order_user_friendly: index + 1
      }
    },
    toggleElementGroup (formElementGroupHash) {
      if (!formElementGroupHash) return

      // Expand
      this.data.period.filter(m => typeof m.collapsed !== 'undefined' && m.form_element_group_hash === formElementGroupHash)
        .forEach(m => {
          m.collapsed = !m.collapsed
        })
    },
    async loadDimensions (force = false) {
      if (!this.$refs.filters?.dates || !this.data.form) return

      // Not needed for eureka automation
      if (window.location.href.includes("src=grafo")) return

      // Keep original values
      if (
        force ||
        !Object.keys(this.dimensions ?? {}).length // Initial load
      ) {
        const query = {
          from: DateTime.fromISO(this.$refs.filters.dates.from).toFormat('yyyy-LL-dd'),
          to: DateTime.fromISO(this.$refs.filters.dates.to).toFormat('yyyy-LL-dd'),
          forms: [this.$refs.filters.item.id],
          filters: this.$refs.filters.dimensionFilters
        }
        let dimensionMetrics = await this.$api.post('/metrics/forms', { ...query, dimensions: ['country_code', 'browser_family', 'device_type'], time_group: 'PERIOD' }, this.$root.client.client_hash)
        dimensionMetrics = dimensionMetrics.data.metrics

        const starts = objectSum(dimensionMetrics, 'starts')
        const errors = objectSum(dimensionMetrics, 'errors')
        const autofills = objectSum(dimensionMetrics, 'autofills')

        this.dimensions = {
          countries: Object.entries(objectSumGrouped(dimensionMetrics, 'country_code', this.toggleStarts ? 'starts' : 'sessions')).map(v => ({ value: v[0], count: v[1] })),
          devices: Object.entries(objectSumGrouped(dimensionMetrics, 'device_type', this.toggleStarts ? 'starts' : 'sessions')).map(v => ({ value: v[0], count: v[1] })),
          browsers: Object.entries(objectSumGrouped(dimensionMetrics, 'browser_family', this.toggleStarts ? 'starts' : 'sessions')).map(v => ({ value: v[0], count: v[1] })),
          errors: [
            { label: 'Yes', value: true, count: errors },
            { label: 'No', value: false, count: (starts - errors) }
          ],
          autofills: [
            { label: 'Used', value: true, count: autofills },
            { label: 'Not used', value: false, count: (starts - autofills) }
          ],
          /* eslint-disable camelcase */
          conversion_path: [
            { value: 'initial_abandoners', count: objectSum(dimensionMetrics, 'initial_abandoners') },
            { value: 'submits_failed', count: objectSum(dimensionMetrics, 'submits_abandon') + objectSum(dimensionMetrics, 'resubmits_success') },
            { value: 'initial_submit_but_abandoned', count: objectSum(dimensionMetrics, 'submits_abandon') },
            { value: 'resubmit_success', count: objectSum(dimensionMetrics, 'resubmits_success') },
            { value: 'converters', count: objectSum(dimensionMetrics, 'conversions') }
          ]
        }
      }
    },
    async loadSessionRecordings () {
      // Not needed for eureka automation
      if (window.location.href.includes("src=grafo")) return

      if (!this.$root.sessionReplayIsAvailable()) return
      const query = {
        from: DateTime.fromISO(this.$refs.filters.dates.from).toFormat('yyyy-LL-dd'),
        to: DateTime.fromISO(this.$refs.filters.dates.to).toFormat('yyyy-LL-dd'),
        time_group: 'period',
        dimensions: ['dropoff_element'],
        forms: [this.$refs.filters.item.id],
        filters: this.$refs.filters.dimensionFilters,
        page: 1,
        per_page: 100000,
      }
      let dimensionMetrics = await this.$api.post('/metrics/session-recordings', query, this.$root.client.client_hash)
      dimensionMetrics = dimensionMetrics.data.metrics

      this.data.sessionRecordingDimensions = objectSumGrouped(dimensionMetrics, 'dropoff_element', this.toggleStarts ? 'starts' : 'sessions')
      this.$forceUpdate()
    },
    getSessionRecordingsForElement (element) {
      if (!this.data.sessionRecordingDimensions || !Object.keys(this.data.sessionRecordingDimensions).length) return false
      if (element?.isGroup === true) return false

      if (typeof this.data.sessionRecordingDimensions[element.name] !== 'undefined') return this.data.sessionRecordingDimensions[element.name]
      if (typeof this.data.sessionRecordingDimensions[element.identifier] !== 'undefined') return this.data.sessionRecordingDimensions[element.identifier]

      function stringWithWildcardsToRegexSafeString (input) {
        if (typeof input === 'undefined' || !input) return
        let result = input.replaceAll('*', '§')
        result = result.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Sanitize regex: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
        result = result.replaceAll('§', '.*?')
        return result
      }

      const nameRegex = new RegExp(stringWithWildcardsToRegexSafeString(element.name), 'i')
      const idRegex = new RegExp(stringWithWildcardsToRegexSafeString(element.identifier), 'i')

      const matchedElements = Object.keys(this.data.sessionRecordingDimensions).find(name => {
        if (element.name?.trim().length && nameRegex.test(name)) {
          return true
        }
        if (element.identifier?.trim().length && idRegex.test(name)) {
          return true
        }
      })

      return matchedElements && matchedElements.length ? matchedElements[0] : false
    },
    async reload (filters) {
      this.loading = true

      if (filters.form) {
        const query = {
          from: DateTime.fromISO(filters.dates.from).toFormat('yyyy-LL-dd'),
          to: DateTime.fromISO(filters.dates.to).toFormat('yyyy-LL-dd'),
          forms: [filters.form.id],
          filters: filters.dimensions
        }
        this.details = {expanded: null, query}
        this.filtersApplied = filters.applied
        this.toggleStarts = (window.localStorage || {})[`toggleStarts_${filters.form.id}`] === '1'

        let form = null
        let fieldMetrics = null;

        [
          this.formElements,
          this.elementGroups,
          this.elementTypes,
          form,
          fieldMetrics
        ] = await Promise.all([
          this.$api.get('/forms/' + filters.form.id + '/elements', this.$root.client.client_hash),
          this.$api.get('/forms/' + filters.form.id + '/elements/groups', this.$root.client.client_hash),
          this.$api.get('/form-element-types', this.$root.client.client_hash),
          this.$api.post('/metrics/forms', { ...query, time_group: 'PERIOD' }, this.$root.client.client_hash),
          this.$api.post('/metrics/fields', { ...query, time_group: 'PERIOD' }, this.$root.client.client_hash)
        ])

        this.formElements = this.formElements.data
        this.elementGroups = this.elementGroups.data.sort((a, b) => a.order - b.order)
        this.elementTypes = this.elementTypes.data.reduce((types, type) => ({ ...types, [type.id]: type }), {})
        form = form.data.metrics
        fieldMetrics = fieldMetrics.data.metrics
        this.fieldMetricsRaw = fieldMetrics

        this.data = { form: form }

        const fieldMetricsData = {
          formHash: filters.form.id,
          form,
          formData: [filters.form],
          period: fieldMetrics
            // Add some data-points
            .map((item, index, all) => (
              {
                ...item,
                form_display_name: filters.form.display_name,
                element: this.getElement(item.element_name),
                /* eslint-disable camelcase */
                element_display_name: this.getElement(item.element_name)?.display_name ?? item.element_name + ' (NEW)',
                form_element_group_hash: this.getElement(item.element_name)?.form_element_group_hash,
                /* eslint-enable camelcase */
                prev: index ? all[index - 1] : { interactions: this.data.form[0].starts },
                next: (index !== (all.length - 1)) ? all[index + 1] : { interactions: this.data.form[0].conversions },
                fill_in_order_user_friendly: index + 1,
                blanks: (form[0].starts - item.interactions),
                blank_rate: Math.round((form[0].starts - item.interactions) / form[0].starts * 10000) / 10000
              }
            ))
        }

        /*
         * Grouping functionality
         */
        // Get all elements that are grouped and delete them
        const fmGroupData = fieldMetricsData.period.filter(metric => metric.form_element_group_hash !== null)
        fieldMetricsData.period = fieldMetricsData.period.filter(metric => metric.form_element_group_hash === null)

        const RowBorderColorFn = function * () {
          const rowBorderColors = ['var(--indigo)', 'var(--cyan)', 'var(--orange)', 'var(--purple)', 'var(--pink)']
          let colors = []
          while (true) {
            if (!colors.length) {
              colors = rowBorderColors
            }
            yield colors.shift()
          }
        }
        const getRowBorderColorFn = RowBorderColorFn()

        for (let elKey = 0; elKey < this.elementGroups.length; elKey++) {
          const elGroup = this.elementGroups[elKey]
          elGroup.fieldMetrics = fmGroupData.filter(g => g.form_element_group_hash === elGroup.id)
          if (!elGroup.fieldMetrics.length) continue

          const totalInteractions = Math.max(...elGroup.fieldMetrics.map(m => m.interactions))

          elGroup.groupMetrics = {
            rowBorderColor: getRowBorderColorFn.next().value,
            isGroup: true,
            collapsed: !(typeof this.$route.query.element !== 'undefined'),
            form_element_group_hash: elGroup.id,
            element_name: elGroup.name,
            element_display_name: elGroup.name,
            element: {
              id: elGroup.id,
              display_name: elGroup.name,
              isGroup: true
            },
            prev: (elKey === 0) ? fieldMetricsData.period[fieldMetricsData.period.length - 1] : this.elementGroups[elKey - 1],
            next: (elKey < this.elementGroups.length - 1) ? this.elementGroups[elKey + 1] : { interactions: form[0].conversions },
            interactions: totalInteractions,
            interaction_time: elGroup.fieldMetrics.map(m => m.interaction_time).reduce((a, b) => a + b, 0),
            hesitation_time: elGroup.fieldMetrics.map(m => m.hesitation_time).reduce((a, b) => a + b, 0),
            total_time: elGroup.fieldMetrics.map(m => m.total_time).reduce((a, b) => a + b, 0),
            dropoffs: elGroup.fieldMetrics.map(m => m.dropoffs).reduce((a, b) => a + b, 0),
            corrections: Math.max(...elGroup.fieldMetrics.map(m => m.corrections)),
            correction_rate: Math.max(...elGroup.fieldMetrics.map(m => m.corrections)) / totalInteractions,
            autofills: Math.max(...elGroup.fieldMetrics.map(m => m.autofills)),
            autofill_rate: Math.max(...elGroup.fieldMetrics.map(m => m.autofills)) / totalInteractions,
            errors: Math.max(...elGroup.fieldMetrics.map(m => m.errors)),
            rage_clicks: Math.max(...elGroup.fieldMetrics.map(m => m.rage_clicks)),
            error_rate: Math.max(...elGroup.fieldMetrics.map(m => m.errors)) / totalInteractions
          }

          // Add group metric
          fieldMetricsData.period.push(elGroup.groupMetrics)

          // Add group element metrics
          for (const fm of elGroup.fieldMetrics) {
            fieldMetricsData.period.push({
              ...fm,
              collapsed: !(typeof this.$route.query.element !== 'undefined'),
              rowBorderColor: elGroup.groupMetrics.rowBorderColor
            })
          }
        }

        this.data = fieldMetricsData
        this.loadDimensions()
        this.loadSessionRecordings()
        this.syncAiChatMetadata()
      } else {
        this.data = null
      }

      setTimeout(() => {
        if (typeof this.$route.query.element !== 'undefined') {
          const field = this.getElement(this.$route.query.element)
          if (this.$refs[`field-${field.id}`]?.length) {
            this.$refs[`field-${field.id}`][0].click()
          } else {
            console.error('Field cannot be found for expanding', this.$route.query.element)
          }
        }
      }) // next tick

      this.loading = false
      this.$nextTick(() => {
        this.syncFieldMetricsScrollListener()
        this.updateStickyHeader()
      })
    },
    syncAiChatMetadata () {
      if (!this.$refs.filters?.dates) return
      this.$root.setAiChatMetadata({
        suggestionsEnabled: true,
        maxSuggestions: 4,
        pageType: 'field-metrics',
        formHash: this.$route.params.formHash || this.data.formHash,
        dateRange: {
          from: this.$refs.filters.dates.from,
          to: this.$refs.filters.dates.to
        },
        filtersApplied: this.filtersApplied,
        selectedDimensions: this.$refs.filters.dimensionFilters || {},
        suggestionButtons: [
          {
            "label": "Explain Field Metrics",
            "prompt": "Explain the meaning of each field metric."
          },
          {
            "label": "Optimise Field Setup",
            "prompt": "Suggest best practices for improving individual field performance, such as label clarity, field order, and autofill configuration."
          },
          {
            "label": "Find Problematic Fields",
            "prompt": "Identify which form fields have the highest hesitation, error, or correction rates, and explain how they affect overall conversions."
          },
          {
            "label": "Recommend Field Improvements",
            "prompt": "Based on the field metrics data, recommend concrete optimisations to reduce drop-offs and improve completion rates."
          }
        ]
      })
    }
  },
  mounted() {
    this.syncAiChatMetadata()
    window.addEventListener('scroll', this.updateStickyHeader, { passive: true })
    window.addEventListener('resize', this.updateStickyHeader, { passive: true })
    this.$nextTick(() => {
      this.syncFieldMetricsScrollListener()
      this.updateStickyHeader()
    })
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.updateStickyHeader)
    window.removeEventListener('resize', this.updateStickyHeader)
    this.fieldMetricsScrollElement?.removeEventListener('scroll', this.updateStickyHeader)
  },
}
</script>

<style scoped>
#fieldMetrics::-webkit-scrollbar {
  display: none;
}

.fieldMetricsHeader--fixed {
  background: #fff;
  overflow: hidden;
  position: fixed;
  margin-top: 0 !important;
  box-shadow: 0 1px 0 rgba(24, 28, 33, 0.08);
  z-index: 1030;
}

.fieldMetricsHeaderAnchor {
  height: 0;
}

.fieldMetricsHeaderContent {
  will-change: transform;
}

.fieldMetricsHeaderSpacer {
  pointer-events: none;
}

.metrics-table {
  overflow-x: scroll !important;
  overflow-y: hidden !important;
}

.metrics-table .ellide-name {
  text-transform: uppercase;
  letter-spacing: 1px;
  width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

/* DIV table setup */
.metrics-rows > div {
  position: relative;
}
.metrics-rows-item {
  position: relative;
  top: 50%;
  transform: translate(0%, -50%);
}
.metrics-rows:nth-of-type(odd):not(.has-dropoff-light) > div {
  box-shadow: inset 0 0 0 1000px rgba(24, 28, 33, 0.025);
}

.has-dropoff-light i, .has-dropoff i {
  color: #ea8383 !important;
}
.has-dropoff {
  box-shadow: inset 0 0 0 1000px rgba(207, 48, 48, 0.125) !important;
}
.has-dropoff-light {
  box-shadow: inset 0 0 0 1000px rgba(207, 48, 48, 0.05);
}

@media (min-width: 992px) {
  .fieldMetricsRow {
    min-width: 100%;
    width: max-content;
  }

  .fieldMetricsFirstColumn {
    flex: 0 0 300px;
    left: 0;
    min-width: 300px;
    position: sticky;
    z-index: 3;
  }

  .fieldMetricsFirstColumn--scrolled {
    background-color: #fff;
  }

  .fieldMetricsFirstColumn--scrolled:not(.fieldMetricsHeader--fixed) {
    box-shadow: 10px 0 14px -14px rgba(24, 28, 33, 0.3);
  }

  .metrics-rows:nth-of-type(odd) .fieldMetricsFirstColumn--scrolled:not(.has-dropoff-light):not(.has-dropoff) {
    box-shadow: inset 0 0 0 1000px rgba(24, 28, 33, 0.025), 10px 0 14px -14px rgba(24, 28, 33, 0.3);
  }

  .fieldMetricsFirstColumn--scrolled.has-dropoff {
    box-shadow: inset 0 0 0 1000px rgba(207, 48, 48, 0.125), 10px 0 14px -14px rgba(24, 28, 33, 0.3) !important;
  }

  .fieldMetricsFirstColumn--scrolled.has-dropoff-light {
    box-shadow: inset 0 0 0 1000px rgba(207, 48, 48, 0.05), 10px 0 14px -14px rgba(24, 28, 33, 0.3);
  }

  .fieldMetricsHeaderFirstColumn {
    z-index: 10;
  }

  .fieldMetricsHeader--fixed .fieldMetricsHeaderFirstColumn {
    z-index: 1;
  }
}

.metrics-rows:not(.isGroup):not(:has(.metrics-rows-item-details)):hover:before {
  content: 'Show details';
  position: absolute;
  left: 0;
  margin-left: 1rem;
  margin-top: 50px;
  z-index: 10;
  font-size: 80%;
  color: rgb(174, 174, 174);
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;
  user-select: none !important;
  pointer-events: none;
}

</style>
